import { ROUND_ORDER, ROUND_PHASES } from "@/types"
import type {
  CardId,
  CardPick,
  Job,
  MoscowBucket,
  Pain,
  Participant,
  Phase,
  Round,
  Session,
} from "@/types"
import { donePhaseKey } from "@/utils/donePhase"
import { isJoinable } from "@/utils/sessionLifecycle"
import type { FamilyNumber } from "@/theme/colors"
import type { SessionState } from "./sessionStore"

/*
 * Derived state only — nothing here is stored.
 *
 * Selectors return primitives or stable store references. Derived objects and
 * arrays are plain functions to avoid unnecessary Zustand re-renders.
 */

/* ---------------------------------------------------------------- selectors */

export const selectSession = (s: SessionState): Session | null => s.session
export const selectParticipants = (s: SessionState): Participant[] => s.participants
export const selectRound = (s: SessionState): Round | null => s.session?.round ?? null
export const selectPhase = (s: SessionState): Phase | null => s.session?.phase ?? null
export const selectSessionStatus = (s: SessionState): Session["status"] | null =>
  s.session?.status ?? null
export const selectConfig = (s: SessionState): Session["config"] | null => s.session?.config ?? null
export const selectTimer = (s: SessionState): Session["timer"] | null => s.session?.timer ?? null
export const selectOverlay = (s: SessionState): Session["overlay"] => s.session?.overlay ?? null
export const selectPains = (s: SessionState): Pain[] => s.pains
export const selectPainsLoaded = (s: SessionState): boolean => s.painsLoaded
export const selectJob = (s: SessionState): Job | null => s.job
export const selectJobs = (s: SessionState): Job[] => s.jobs
export const selectPicks = (s: SessionState): CardPick[] => s.picks

/** True once a job is open, i.e. the room is working a specific problem. */
export const selectHasJob = (s: SessionState): boolean => s.job !== null

/**
 * Whether the current job reads as a complete sentence. What counts as complete
 * depends on the session's format — see `jobHolds`. The Job round's lock gates
 * on this; derived, never stored.
 */
export const selectJobHolds = (s: SessionState): boolean => {
  const userStoryEnabled = s.session?.config.userStoryEnabled ?? true
  return s.job !== null && jobHolds(s.job, userStoryEnabled)
}

const selectCurrentParticipant = (s: SessionState): Participant | null =>
  s.participants.find((p) => p.id === s.currentParticipantId) ?? null

/**
 * Whether this browser may drive the session.
 *
 * The signed-in account, tested against the session's own list of facilitators.
 * Not a local flag and not a role someone picks: the same check runs in the
 * Firestore rules, so a client that lies about it gets its writes refused rather
 * than a different screen.
 */
export const selectIsFacilitator = (s: SessionState): boolean =>
  s.signedInUid !== null && (s.session?.facilitatorIds.includes(s.signedInUid) ?? false)

/**
 * Who this browser writes as: a guest's participant id, or the facilitator's
 * uid. Both go into a pain point's `authorId` and a pick's document id, which is
 * why they share one accessor — the two never collide, because a uid comes from
 * Google and a guest id from `crypto.randomUUID`.
 */
export const selectMyAuthorId = (s: SessionState): string | null =>
  selectIsFacilitator(s) ? s.signedInUid : s.currentParticipantId

/** What the facilitator is called wherever a name is needed and there is no
 *  participant document to read one from. */
export const FACILITATOR_NAME = "Facilitator"

/** How this browser is named on anything it writes. */
export const selectMyName = (s: SessionState): string =>
  selectCurrentParticipant(s)?.name ?? (selectIsFacilitator(s) ? FACILITATOR_NAME : "")

export const selectParticipantCount = (s: SessionState): number => s.participants.length

export const selectReadyCount = (s: SessionState): number =>
  s.participants.filter((p) => p.ready).length

export const selectAllReady = (s: SessionState): boolean =>
  s.participants.length > 0 && s.participants.every((p) => p.ready)

/** Whether THIS browser's guest has pressed "I'm ready". */
export const selectAmIReady = (s: SessionState): boolean =>
  selectCurrentParticipant(s)?.ready ?? false

/* ---------------------------------------------------------------- lifecycle */

/** The session is still waiting for the facilitator to start it. */
export const selectIsPending = (s: SessionState): boolean => s.session?.status === "pending"
export const selectIsCompleted = (s: SessionState): boolean => s.session?.status === "completed"
/**
 * Whether anything may still be written into the session.
 *
 * There is deliberately no `selectIsExpired` beside this. Expiry is a moment
 * passing rather than a write, so a selector reading it answers only when the
 * store changes — an open session would sit there saying "live" for hours after
 * its deadline, with every control still offered. A screen that has to render
 * the difference takes `now` from `useExpiryTick` and calls
 * `isExpired`/`sessionState` with it. This one is left because it is asked
 * DURING a write, where "as of the last snapshot" is the right reading.
 */
export const selectIsWritable = (s: SessionState): boolean => isJoinable(s.session)

/* --------------------------------------------------------------- the gates */

/**
 * The gate the room is on right now, or null before a session has loaded.
 *
 * Every "done" question below is asked against this key, so a participant who
 * finished the previous gate does not count towards this one and job two's card
 * pick starts with nobody done. Nothing is ever reset — see `utils/donePhase`.
 */
export const selectCurrentDoneKey = (s: SessionState): string | null =>
  s.session ? donePhaseKey(s.session.currentJobId, s.session.round, s.session.phase) : null

export const selectDoneCount = (s: SessionState): number => {
  const key = selectCurrentDoneKey(s)
  return key === null ? 0 : s.participants.filter((p) => p.donePhase === key).length
}

/** What the facilitator's "everyone is done, move on" gate reads. */
export const selectAllDone = (s: SessionState): boolean => {
  const key = selectCurrentDoneKey(s)
  return (
    key !== null && s.participants.length > 0 && s.participants.every((p) => p.donePhase === key)
  )
}

/** Whether THIS browser's participant has finished the current gate. */
export const selectAmIDone = (s: SessionState): boolean => {
  const key = selectCurrentDoneKey(s)
  return key !== null && selectCurrentParticipant(s)?.donePhase === key
}

/* ---------------------------------------------------------------- surfaces */

/**
 * Which of the screens this browser is looking at.
 *
 * Every surface renders the same round from the same session document; what
 * differs is who may act. Derived, because it is already implied by identity
 * plus the roster:
 *
 *   facilitator — signed in and named in the session's facilitators
 *   participant — in the roster, does the work
 *   spectator   — no identity at all: the link, opened by someone who has not
 *                 taken a seat. Nothing in the room renders for them — `Session`
 *                 sends them to the join screen to give a name, because work
 *                 that cannot be attributed to anybody is work the room loses.
 *   removed     — this browser holds a guest id the roster no longer contains
 *   resolving   — identity restored from storage, roster not yet delivered
 */
type SessionSurface = "facilitator" | "participant" | "spectator" | "removed" | "resolving"

export const selectSurface = (s: SessionState): SessionSurface => {
  // Checked first, and deliberately ahead of any guest id: a facilitator who
  // once joined their own session as a guest from this browser is still the
  // facilitator, and localStorage is shared across tabs.
  if (selectIsFacilitator(s)) return "facilitator"
  if (!s.currentParticipantId) return "spectator"
  if (selectCurrentParticipant(s)) return "participant"
  // Only claim someone was removed once the roster has actually arrived.
  return s.rosterLoaded ? "removed" : "resolving"
}

export const selectIsLive = (s: SessionState): boolean => s.connection === "live"
export const selectIsLoading = (s: SessionState): boolean =>
  s.connection === "idle" || s.connection === "connecting"
/**
 * Firestore answered, and there is no such session — a mistyped link, a stale
 * bookmark, or a session that has been deleted. A screen must render this as its
 * own state: it is neither loading (nothing more is coming) nor an error
 * (nothing is broken).
 */
export const selectSessionMissing = (s: SessionState): boolean => s.connection === "missing"
/**
 * A listener has died. Terminal — Firestore does not retry these — so the only
 * way out is a reload. Distinct from `selectError`, which also carries failed
 * writes, where the session itself is still live.
 */
export const selectConnectionFailed = (s: SessionState): boolean => s.connection === "error"
export const selectError = (s: SessionState): string | null => s.error

/*
 * The same three states for the customer subscription, read from its own status
 * rather than the session's. The customer screen has no session, so the session
 * status there is whatever it was left at — which is what made a missing
 * customer sit on "Loading." forever.
 */
export const selectCustomerLoading = (s: SessionState): boolean =>
  s.customerConnection === "idle" || s.customerConnection === "connecting"
export const selectCustomerFailed = (s: SessionState): boolean => s.customerConnection === "error"
export const selectCustomerError = (s: SessionState): string | null => s.customerError

/* ------------------------------------------------------ pure derivations */

/**
 * Whether the job is written out.
 *
 * In user-story format all three parts are required — a job with no `why` names
 * a person and a want but no reason the work matters, and the whole deck gets
 * played against that sentence. With the format switched off the room writes one
 * plain sentence, and requiring `why` would block a lock on a field the screen
 * never shows.
 */
export function jobHolds(job: Job | null, userStoryEnabled = true): boolean {
  if (!job) return false
  if (!userStoryEnabled) return job.what.trim() !== ""
  return job.who.trim() !== "" && job.what.trim() !== "" && job.why.trim() !== ""
}

/**
 * "As a <who>, I want <what>, so that <why>." — or just the plain sentence when
 * the user-story format is off. Empty until the job holds.
 */
export function jobSentence(job: Job | null, userStoryEnabled = true): string {
  if (!jobHolds(job, userStoryEnabled)) return ""
  const held = job as Job
  if (!userStoryEnabled) return held.what.trim().replace(/\.?$/, ".")
  return `As a ${held.who}, I want ${held.what}, so that ${held.why}.`
}

/** One run of the job sentence: scaffold words, or a written-in value. */
export interface SentencePart {
  text: string
  /** The facilitator's own words — set bold; the scaffold stays regular. */
  strong: boolean
}

/**
 * `jobSentence` split so only the written-in values carry the bold display face
 * and the "As a … I want … so that …" scaffold reads regular. Must stay word-for-
 * word identical to `jobSentence`, which is still the one source for plain-text
 * export. Empty until the job holds.
 */
export function jobSentenceParts(job: Job | null, userStoryEnabled = true): SentencePart[] {
  if (!jobHolds(job, userStoryEnabled)) return []
  const held = job as Job
  if (!userStoryEnabled) return [{ text: held.what.trim().replace(/\.?$/, "."), strong: true }]
  return [
    { text: "As a ", strong: false },
    { text: held.who, strong: true },
    { text: ", I want ", strong: false },
    { text: held.what, strong: true },
    { text: ", so that ", strong: false },
    { text: held.why, strong: true },
    { text: ".", strong: false },
  ]
}

/* ------------------------------------------------------------------ pains */

/** What one person wrote, in board order. The limit is applied against this. */
export function painsBy(pains: Pain[], authorId: string | null): Pain[] {
  if (!authorId) return []
  return pains.filter((pain) => pain.authorId === authorId)
}

/**
 * Where each pain sits on the board, by id.
 *
 * `pains` is already in creation order, so this is just its index — but the
 * index has to be looked up rather than taken from whatever list is being
 * rendered. Three screens draw a note from a list that is NOT the board:
 * the tally re-sorts by votes, the composer's stack is one author's notes with
 * a blank sheet on top, and the job shows only the pains it came from. All of
 * them need the board number, because that is what `noteTints.ts` colours a
 * note by and a note may not change colour when it moves.
 */
export function painPositions(pains: Pain[]): Map<string, number> {
  return new Map(pains.map((pain, index) => [pain.id, index]))
}

/** Whether this person may write another, given the session's limit. */
export function canAddPain(pains: Pain[], authorId: string | null, limit: number): boolean {
  if (!authorId) return false
  return painsBy(pains, authorId).length < limit
}

/** How many votes this person has already spent, across the whole board. */
export function votesSpent(pains: Pain[], voterId: string | null): number {
  if (!voterId) return 0
  return pains.filter((pain) => pain.voterIds.includes(voterId)).length
}

export function votesLeft(pains: Pain[], voterId: string | null, limit: number): number {
  return Math.max(limit - votesSpent(pains, voterId), 0)
}

/**
 * The board sorted by votes, most first.
 *
 * Ties keep board order — creation order — rather than being broken arbitrarily,
 * so two pains on three votes each appear in the order the room wrote them and
 * every client shows the same list.
 */
export function painsByVotes(pains: Pain[]): Pain[] {
  return pains
    .map((pain, index) => ({ pain, index }))
    .sort((a, b) => b.pain.voterIds.length - a.pain.voterIds.length || a.index - b.index)
    .map((entry) => entry.pain)
}

/**
 * The vote band a pain landed in — the tally's own ranking, independent of
 * phase. `PainsBoard` badges it once the tally is revealed; `Job` reads the
 * same shape so the note a job was named from keeps wearing the label the
 * room saw it win, instead of forgetting the tally the moment the round ends.
 */
export interface PainRank {
  band: "top" | "runnerUp" | "third"
  /** Only meaningful on `"top"` — a tie has no single winner. */
  tied: boolean
}

export function painRank(pains: Pain[], painId: string): PainRank | null {
  const count = pains.find((pain) => pain.id === painId)?.voterIds.length ?? 0
  if (count === 0) return null
  const rankedCounts = Array.from(
    new Set(pains.map((pain) => pain.voterIds.length).filter((n) => n > 0))
  ).sort((a, b) => b - a)
  const band = rankedCounts.indexOf(count)
  if (band > 2) return null
  const topVotes = rankedCounts[0]
  return {
    band: band === 0 ? "top" : band === 1 ? "runnerUp" : "third",
    tied: band === 0 && pains.filter((pain) => pain.voterIds.length === topVotes).length > 1,
  }
}

/**
 * Names for a list of ids — voters on a pain, pickers of a card, whoever is
 * done.
 *
 * Everything multiplayer is stored as ids, and every avatar needs a name, so the
 * join happens here rather than in each screen. An id that is not on the roster
 * is the facilitator: they write pains and pick cards under their uid but have
 * no participant document, so they can never be looked up. Anyone else missing
 * has left the session, and is still shown — their vote or their pick stands.
 */
export function peopleFor(
  participants: Participant[],
  ids: readonly string[]
): { id: string; name: string; family?: FamilyNumber }[] {
  return ids.map((id) => {
    const index = participants.findIndex((participant) => participant.id === id)
    const found = index >= 0 ? participants[index] : undefined
    return {
      id,
      name: found?.name || FACILITATOR_NAME,
      // Seat-ring family, by roster position — same convention as
      // `RoundControls`/`DoneRoster`/`SeatRing`, so a voter's avatar never
      // disagrees in colour with the same person shown elsewhere in the room.
      family: index >= 0 ? (((index % 7) + 1) as FamilyNumber) : undefined,
    }
  })
}

/** The pains a job was opened for, in board order. */
export function painsForJob(pains: Pain[], job: Job | null): Pain[] {
  if (!job) return []
  const wanted = new Set(job.painIds)
  return pains.filter((pain) => wanted.has(pain.id))
}

/* ------------------------------------------------------------------ cards */

export interface CardTally {
  cardId: CardId
  /** How many people picked it. */
  count: number
  /** Who, so the facilitator can ask them why. */
  pickedBy: string[]
}

/**
 * What the room picked, most-picked first — the list the facilitator curates
 * from.
 *
 * Ties break on card id so the order is stable between clients and does not
 * reshuffle as picks arrive. A card nobody picked is simply absent; the
 * facilitator adds those by hand.
 */
export function tallyPicks(picks: CardPick[]): CardTally[] {
  const counts = new Map<CardId, string[]>()
  for (const pick of picks) {
    for (const cardId of pick.cardIds) {
      const pickedBy = counts.get(cardId)
      if (pickedBy) pickedBy.push(pick.id)
      else counts.set(cardId, [pick.id])
    }
  }
  return [...counts.entries()]
    .map(([cardId, pickedBy]) => ({ cardId, count: pickedBy.length, pickedBy }))
    .sort((a, b) => b.count - a.count || a.cardId.localeCompare(b.cardId))
}

/** One person's current selection, for the picker screen. */
export function picksBy(picks: CardPick[], participantId: string | null): CardId[] {
  if (!participantId) return []
  return picks.find((pick) => pick.id === participantId)?.cardIds ?? []
}

/** How many people have made any selection at all — the tally's denominator. */
export function pickersDone(picks: CardPick[]): number {
  return picks.filter((pick) => pick.cardIds.length > 0).length
}

/* ------------------------------------------------------------- priorities */

/**
 * The shortlist split by MoSCoW bucket, each in shortlist order.
 *
 * Shortlist order, not placement order: the facilitator arranged the list and
 * moves cards between buckets while the room watches, so a card must not jump
 * position within its column when its note is edited.
 */
export function cardsByBucket(job: Job | null): Record<MoscowBucket, CardId[]> {
  const out: Record<MoscowBucket, CardId[]> = { must: [], should: [], could: [], wont: [] }
  if (!job) return out
  for (const cardId of job.cardIds) {
    const bucket = job.placements[cardId]?.bucket
    if (bucket) out[bucket].push(cardId)
  }
  return out
}

/** Shortlisted cards the facilitator has not filed yet. */
export function unplacedCards(job: Job | null): CardId[] {
  if (!job) return []
  return job.cardIds.filter((cardId) => !job.placements[cardId])
}

/** What the deep dive works through: the Must haves, in shortlist order. */
export function mustHaveCards(job: Job | null): CardId[] {
  return cardsByBucket(job).must
}

/* ------------------------------------------------------------------- flow */

/**
 * Rounds actually in play. Two of the seven are optional exercises, so this is
 * the only place that decides whether a round exists — progress, the timeline and
 * every "what comes next" answer derive from it.
 *
 * The job, cards and priorities rounds are not optional: every pick, placement
 * and deep-dive answer hangs off a job, so a session without one collects
 * nothing. Only the warm-up (writing and voting on pains) and the deep dive can
 * be dropped.
 */
export function activeRounds(config: Session["config"] | null): Round[] {
  const enabled: Partial<Record<Round, boolean>> = {
    /*
     * Both default to on, matching `DEFAULT_SESSION_CONFIG` and the prototype's
     * `!== false` tests. The fallback only fires for a session document written
     * before the flag existed, and for those the generous reading is right: the
     * round was in play when they ran.
     */
    pains: config?.painsEnabled ?? true,
    deep: config?.deepDiveEnabled ?? true,
  }
  return ROUND_ORDER.filter((round) => enabled[round] ?? true)
}

export function phasesForRound(round: Round): readonly Phase[] {
  return ROUND_PHASES[round]
}

interface Progress {
  roundIndex: number
  roundCount: number
  phaseIndex: number
  phaseCount: number
  /** 0–1 across the whole session, counting every phase of every active round. */
  ratio: number
}

/** Progress through the session, derived from round + phase. */
export function getProgress(session: Session | null): Progress {
  const rounds = activeRounds(session?.config ?? null)
  const empty: Progress = {
    roundIndex: 0,
    roundCount: rounds.length,
    phaseIndex: 0,
    phaseCount: 0,
    ratio: 0,
  }
  if (!session) return empty

  const roundIndex = Math.max(
    rounds.findIndex((r) => r === session.round),
    0
  )
  const phases = phasesForRound(session.round)
  const phaseIndex = Math.max(
    phases.findIndex((p) => p === session.phase),
    0
  )

  const totalPhases = rounds.reduce((total, round) => total + phasesForRound(round).length, 0)
  const completedPhases =
    rounds.slice(0, roundIndex).reduce((total, round) => total + phasesForRound(round).length, 0) +
    phaseIndex

  return {
    roundIndex,
    roundCount: rounds.length,
    phaseIndex,
    phaseCount: phases.length,
    ratio: totalPhases === 0 ? 0 : completedPhases / totalPhases,
  }
}

export interface Position {
  round: Round
  phase: Phase
}

/**
 * Returns the next phase or active round.
 * Skips disabled rounds and returns null at the end.
 */
export function nextPosition(session: Session | null): Position | null {
  if (!session) return null

  const phases = phasesForRound(session.round)
  const phaseIndex = phases.indexOf(session.phase)
  if (phaseIndex >= 0 && phaseIndex < phases.length - 1) {
    return { round: session.round, phase: phases[phaseIndex + 1] }
  }

  const rounds = activeRounds(session.config)
  const roundIndex = rounds.indexOf(session.round)
  const nextRound = roundIndex >= 0 ? rounds[roundIndex + 1] : undefined
  if (!nextRound) return null

  return { round: nextRound, phase: phasesForRound(nextRound)[0] }
}

/**
 * Where a round stands relative to the room's own position.
 *
 *   done   the room has been through it — it can be opened and read
 *   live   the room is standing in it
 *   ahead  the room has not reached it, and the rail will not open it
 *
 * The rail navigates on this and nothing else. It is the same answer for the
 * facilitator and for a guest, because looking back is a personal act now: the
 * rail changes only the screen it was pressed on, and what moves the ROOM is the
 * controls that commit the work. That is why "ahead" is unreachable rather than
 * merely discouraged — a jump forward would land the room in a round whose
 * documents nothing has prepared.
 */
export type FlowStanding = "done" | "live" | "ahead"

export function roundStanding(session: Session | null, round: Round): FlowStanding {
  if (!session) return "ahead"
  const rounds = activeRounds(session.config)
  const here = rounds.indexOf(session.round)
  const there = rounds.indexOf(round)
  // A round that is switched off is not behind the room, it is not in the flow.
  if (here < 0 || there < 0) return "ahead"
  if (there < here) return "done"
  return there === here ? "live" : "ahead"
}

/**
 * Whether a position may be opened for review: strictly behind the room's own.
 *
 * The one rule, in one place — a finished round at any of its phases, or an
 * earlier phase of the round in progress. Everything else, including the
 * position the room is actually on, is not a review; `SessionView.live()` is how
 * a screen goes back to that.
 */
export function canReviewPosition(session: Session | null, round: Round, phase: Phase): boolean {
  const standing = roundStanding(session, round)
  if (standing === "ahead") return false
  if (standing === "done") return phasesForRound(round).includes(phase)

  const phases = phasesForRound(round)
  const at = phases.indexOf(session?.phase ?? phases[0])
  const want = phases.indexOf(phase)
  return want >= 0 && at >= 0 && want < at
}

/** Where a finished round is opened: the state the room left it in. */
export function lastPhaseOf(round: Round): Phase {
  const phases = phasesForRound(round)
  return phases[phases.length - 1]
}

/** Where "back" goes. Mirror of `nextPosition`, for correcting a mis-click. */
export function previousPosition(session: Session | null): Position | null {
  if (!session) return null

  const phases = phasesForRound(session.round)
  const phaseIndex = phases.indexOf(session.phase)
  if (phaseIndex > 0) return { round: session.round, phase: phases[phaseIndex - 1] }

  const rounds = activeRounds(session.config)
  const roundIndex = rounds.indexOf(session.round)
  if (roundIndex <= 0) return null

  const previousRound = rounds[roundIndex - 1]
  const previousPhases = phasesForRound(previousRound)
  return { round: previousRound, phase: previousPhases[previousPhases.length - 1] }
}
